/*
  Getting an id_token out of Google or Apple, in the browser.

  Both providers hand the page a signed token that names the person; the server verifies it
  and decides what that means. Neither flow needs a client secret, which is what keeps Apple
  out of the business of a .p8 file and a six-monthly rotation.

  The two libraries behave differently and it is worth saying why the code is not symmetric.

  Google will not give an id_token to an arbitrary button. One Tap can be suppressed for
  reasons the page never learns about, so the reliable path — and the one Google's branding
  rules ask for — is to render Google's own button and take the credential from its
  callback. That is why the Google path needs a container element and the Apple path does
  not.

  Apple's SDK does expose a callable sign-in, and so does Facebook's, so those two buttons
  are ordinary elements the card owns. All three now wear the same livery; Google's own
  button is still what gets clicked, drawn invisibly over the face of ours. usePopup keeps
  Apple's answer in this page rather than posting a form back to the server, which would mean
  a second endpoint and a redirect round trip for no benefit.
*/

const GOOGLE_SRC = 'https://accounts.google.com/gsi/client'
const APPLE_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'

/** Cached by src, so switching modes in the card does not refetch either library. */
const loading = new Map<string, Promise<void>>()

function loadScript(src: string): Promise<void> {
  const existing = loading.get(src)
  if (existing) return existing

  const started = new Promise<void>((resolve, reject) => {
    const el = document.createElement('script')
    el.src = src
    el.async = true
    el.onload = () => resolve()
    el.onerror = () => {
      // Let a later attempt try again. A blocked or offline first load should not
      // permanently disable the button for the rest of the session.
      loading.delete(src)
      reject(new Error('Could not load that sign-in library.'))
    }
    document.head.appendChild(el)
  })

  loading.set(src, started)
  return started
}

// ─── Google ───────────────────────────────────────────────────────────────

interface GoogleId {
  initialize(config: { client_id: string; callback: (r: { credential?: string }) => void }): void
  renderButton(parent: HTMLElement, options: Record<string, unknown>): void
}

function googleApi(): GoogleId | null {
  return (window as unknown as { google?: { accounts?: { id?: GoogleId } } })
    .google?.accounts?.id ?? null
}

/** The client id already registered with Google, so it is only registered once. */
let initializedFor: string | null = null

/** Where a credential goes now. Swapped on each mount; see mountGoogleButton. */
let handler: ((idToken: string) => void) | null = null

/**
 * Draws Google's button into `parent` and resolves each time somebody completes the flow.
 *
 * `onToken` rather than a promise because the button can be used more than once: a first
 * attempt that fails validation server-side should still leave a working button behind.
 */
export async function mountGoogleButton(
  parent: HTMLElement,
  clientId: string,
  onToken: (idToken: string) => void,
): Promise<void> {
  await loadScript(GOOGLE_SRC)

  const api = googleApi()
  if (!api) throw new Error('Google sign-in did not load.')

  /*
    initialize() is global to the page and Google logs a warning every time it is called
    again, keeping only the last registration. The button is drawn more than once now (the
    signin and signup modes share this card, and a resize redraws it), so it is registered
    once per client id and the live handler is swapped underneath instead.
  */
  handler = onToken
  if (initializedFor !== clientId) {
    api.initialize({
      client_id: clientId,
      callback: r => { if (r.credential) handler?.(r.credential) },
    })
    initializedFor = clientId
  }

  parent.replaceChildren()

  /*
    Still Google's own button, and still the thing being clicked: it is drawn here and then
    laid over ours at opacity 0, because Google will not issue a credential from a button it
    did not draw. The theme and wording below are therefore never seen. They are set anyway,
    so that if the overlay CSS is ever broken what appears is a real, compliant Sign in with
    Google button rather than nothing.
  */
  api.renderButton(parent, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'continue_with',
    shape: 'rectangular',
    logo_alignment: 'center',
    width: googleWidth(parent),
  })
}

/**
 * Google's width is not the width you get, and the gap is where the button went inset.
 *
 * Measured against the live card: Google draws the button at exactly the width it is handed,
 * inside an iframe 20px wider carrying `margin: -2px -10px`. The laid-out footprint is
 * therefore the requested width, and the painted iframe hangs 10px of transparent gutter
 * past each edge. Passing box minus 20 lined that painted box up with the card and left the
 * visible button 10px short on both sides, which is what it looked like.
 *
 * Clamped to the 200 to 400 Google accepts. The floor is unreachable on this card. The
 * ceiling matters now that the button is laid over one of ours: past 400 the face would be
 * wider than the thing receiving the click. Nothing renders it that wide (the landing
 * page's auth panel is 380, the standalone card 360).
 */
function googleWidth(parent: HTMLElement): number {
  const box = Math.round(parent.getBoundingClientRect().width)
  return Math.max(200, Math.min(400, box))
}

// ─── Apple ────────────────────────────────────────────────────────────────

interface AppleAuth {
  init(config: Record<string, unknown>): void
  signIn(): Promise<{ authorization?: { id_token?: string } }>
}

function appleApi(): AppleAuth | null {
  return (window as unknown as { AppleID?: { auth?: AppleAuth } }).AppleID?.auth ?? null
}

/**
 * Opens Apple's popup and resolves with the id_token.
 *
 * `email` and `name` are requested because Apple releases them exactly once, on the very
 * first authorisation for a given Services ID. Every sign-in after that returns a token
 * with a subject and no address, which is why the server treats the subject as the key and
 * refuses to *create* an account without a confirmed address.
 */
export async function signInWithApple(servicesId: string): Promise<string> {
  await loadScript(APPLE_SRC)

  const api = appleApi()
  if (!api) throw new Error('Apple sign-in did not load.')

  api.init({
    clientId: servicesId,
    scope: 'name email',
    redirectURI: window.location.origin,
    usePopup: true,
  })

  const result = await api.signIn()
  const token = result?.authorization?.id_token
  if (!token) throw new Error('Apple did not return a sign-in token.')

  return token
}

// ─── Facebook ─────────────────────────────────────────────────────────────

const FACEBOOK_SRC = 'https://connect.facebook.net/en_US/sdk.js'

interface FacebookAuth {
  init(config: Record<string, unknown>): void
  login(
    callback: (r: { authResponse?: { accessToken?: string } | null; status?: string }) => void,
    options: { scope: string },
  ): void
}

function facebookApi(): FacebookAuth | null {
  return (window as unknown as { FB?: FacebookAuth }).FB ?? null
}

/** Init happens once per page, and the button waits on this rather than repeating it. */
let facebookReady: Promise<void> | null = null

/**
 * Loads and initialises Facebook's SDK ahead of the click, not during it.
 *
 * FB.login opens a popup, and a popup survives only if it opens inside the gesture that
 * asked for it. Fetching a script first spends that gesture and the browser blocks the
 * window, so the library is pulled as soon as the card learns Facebook is configured and
 * the button then calls straight into it.
 *
 * Facebook is also the one provider whose SDK refuses to start without being told a Graph
 * version, which is why that travels from the server alongside the app id.
 */
export function preloadFacebook(appId: string, version: string): Promise<void> {
  facebookReady ??= loadScript(FACEBOOK_SRC).then(() => {
    const api = facebookApi()
    if (!api) throw new Error('Facebook sign-in did not load.')

    // cookie:false because the session here is our own; Facebook has no business setting
    // one. xfbml:false because there are no Facebook social plugins on this page to parse.
    api.init({ appId, version, cookie: false, xfbml: false })
  })

  return facebookReady
}

/**
 * Opens Facebook's dialog and resolves with an access token.
 *
 * Unlike Google and Apple this is not a signed assertion and means nothing on its own. The
 * server hands it to Graph, asked as the app, and that is what decides who it belongs to.
 *
 * `email` is requested and may still not arrive: a Facebook account can be registered on a
 * phone number, and the person can untick the address on the dialog. The server treats that
 * the same way it treats Apple withholding one.
 */
export async function signInWithFacebook(): Promise<string> {
  await preloadFacebookOrThrow()

  const api = facebookApi()
  if (!api) throw new Error('Facebook sign-in did not load.')

  return new Promise<string>((resolve, reject) => {
    api.login(
      r => {
        const token = r?.authResponse?.accessToken
        if (token) return resolve(token)

        // No token and no error: the dialog was dismissed. Given its own code so the card
        // can stay quiet about it, the way it does for Apple.
        reject(Object.assign(
          new Error('Facebook did not return a sign-in token.'), { error: 'facebook_dismissed' }))
      },
      { scope: 'email' },
    )
  })
}

function preloadFacebookOrThrow(): Promise<void> {
  if (!facebookReady) throw new Error('Facebook sign-in is not ready yet.')
  return facebookReady
}

// ─── Shared ───────────────────────────────────────────────────────────────

/**
 * True when the person closed the provider's popup themselves.
 *
 * Worth telling apart: a cancellation is not a failure and showing "that did not work"
 * over a window somebody deliberately shut is just noise.
 */
export function isCancelled(error: unknown): boolean {
  const code = (error as { error?: string })?.error
  return code === 'popup_closed_by_user' ||
         code === 'user_cancelled_authorize' ||
         code === 'facebook_dismissed'
}
